import { readonly, ref } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const THEME_STORAGE_KEY = 'issen-web-theme'
const DARK_THEME_CLASS = 'dark'
const DEFAULT_THEME_MODE: ThemeMode = 'system'
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'
const DEFAULT_THEME_COLOR_LIGHT = 'hsl(53 44% 96%)'
const DEFAULT_THEME_COLOR_DARK = 'hsl(222 24% 9%)'

const themeModeRef = ref<ThemeMode>(DEFAULT_THEME_MODE)
const resolvedThemeRef = ref<ResolvedTheme>('light')

let mediaQueryList: MediaQueryList | null = null
let hasRegisteredSystemListener = false

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system'
}

function getStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME_MODE
  }

  const storedThemeMode = window.localStorage.getItem(THEME_STORAGE_KEY)
  if (isThemeMode(storedThemeMode)) {
    return storedThemeMode
  }

  return DEFAULT_THEME_MODE
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return 'light'
  }

  return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light'
}

function resolveTheme(themeMode: ThemeMode): ResolvedTheme {
  if (themeMode === 'system') {
    return getSystemTheme()
  }

  return themeMode
}

function updateThemeColorMetaTag(): void {
  if (typeof document === 'undefined') {
    return
  }

  const themeColorMetaTag = document.querySelector('meta[name="theme-color"]')
  if (!(themeColorMetaTag instanceof HTMLMetaElement)) {
    return
  }

  const rootStyles = getComputedStyle(document.documentElement)
  const themeColor = rootStyles.getPropertyValue('--theme-color').trim()
  if (themeColor.length > 0) {
    themeColorMetaTag.content = themeColor
    return
  }

  themeColorMetaTag.content = resolvedThemeRef.value === 'dark'
    ? DEFAULT_THEME_COLOR_DARK
    : DEFAULT_THEME_COLOR_LIGHT
}

function applyThemeMode(themeMode: ThemeMode): void {
  themeModeRef.value = themeMode

  const resolvedTheme = resolveTheme(themeMode)
  resolvedThemeRef.value = resolvedTheme

  if (typeof document === 'undefined') {
    return
  }

  const rootElement = document.documentElement
  rootElement.classList.toggle(DARK_THEME_CLASS, resolvedTheme === 'dark')
  rootElement.style.colorScheme = resolvedTheme
  rootElement.dataset.themeMode = themeMode
  rootElement.dataset.theme = resolvedTheme

  updateThemeColorMetaTag()
}

function handleSystemThemeChange(): void {
  if (themeModeRef.value !== 'system') {
    return
  }

  applyThemeMode(themeModeRef.value)
}

function ensureSystemThemeListener(): void {
  if (typeof window === 'undefined') {
    return
  }

  if (hasRegisteredSystemListener) {
    return
  }

  mediaQueryList = window.matchMedia(DARK_MEDIA_QUERY)
  mediaQueryList.addEventListener('change', handleSystemThemeChange)
  hasRegisteredSystemListener = true
}

export function initializeTheme(): void {
  ensureSystemThemeListener()
  applyThemeMode(getStoredThemeMode())
}

export function setThemeMode(themeMode: ThemeMode): void {
  applyThemeMode(themeMode)

  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(THEME_STORAGE_KEY, themeMode)
}

export function useTheme(): {
  themeMode: Readonly<typeof themeModeRef>
  resolvedTheme: Readonly<typeof resolvedThemeRef>
  setThemeMode: typeof setThemeMode
} {
  return {
    themeMode: readonly(themeModeRef),
    resolvedTheme: readonly(resolvedThemeRef),
    setThemeMode,
  }
}
